<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useRouter } from 'vue-router';

import { eqsApi } from '@/api/eqs';
import { notify } from '@/services/notice';
import type { AppNotification } from '@/types/domain';
import { errorMessage } from '@/utils/format';
import {
  groupByProject,
  notificationTypeLabel as typeLabel,
  notificationWhen as when,
  stackRepeats,
} from '@/utils/notifications';

/**
 * 站内通知入口。
 *
 * 之前这套通知只写库不出面：库里 12659 条、54 个人全部未读，
 * 唯一的出口是每日邮件摘要，而摘要在正式切换前是关着的——
 * 催办、指派提醒、评审待办写进去谁也看不到（2026-08-13 盘点发现）。
 *
 * 只读 + 标已读，不在这里做业务操作：点一条就跳到它对应的记录，
 * 该干什么在那边干，避免把通知面板做成第二个工作台。
 */
const router = useRouter();

const open = ref(false);
const loading = ref(false);
const unread = ref(0);
const items = ref<AppNotification[]>([]);
const panel = ref<HTMLElement | null>(null);
/** 只看未读；默认开着——通知面板的用处是「还有什么没处理」 */
const unreadOnly = ref(true);

/** 60 秒轮询角标。没有 WebSocket，轮询足够：这类提醒不要求秒级 */
const POLL_MS = 60_000;
let timer: number | null = null;

async function refresh(): Promise<void> {
  loading.value = true;
  try {
    const payload = await eqsApi.notifications(unreadOnly.value, 50);
    unread.value = payload.unread;
    items.value = payload.items;
  } catch {
    // 通知取不到不该打断正在做的事，静默退回
  } finally {
    loading.value = false;
  }
}

async function toggle(): Promise<void> {
  open.value = !open.value;
  if (open.value) await refresh();
}

async function markOne(item: AppNotification): Promise<void> {
  if (item.readAt) return;
  try {
    await eqsApi.markNotificationsRead([item.id]);
    item.readAt = new Date().toISOString();
    unread.value = Math.max(0, unread.value - 1);
    if (unreadOnly.value) items.value = items.value.filter((row) => row.id !== item.id);
  } catch (caught) {
    notify('error', '标记已读失败', errorMessage(caught));
  }
}

async function markAll(): Promise<void> {
  try {
    const result = await eqsApi.markAllNotificationsRead();
    notify('success', `已全部标为已读`, `共 ${result.updated} 条。`);
    await refresh();
  } catch (caught) {
    notify('error', '操作失败', errorMessage(caught));
  }
}

/** 点通知就去它指向的记录；先标已读再跳，免得跳走后角标还挂着 */
async function openItem(item: AppNotification): Promise<void> {
  await markOne(item);
  if (!item.link) return;
  open.value = false;
  await router.push(item.link);
}

function onDocumentClick(event: MouseEvent): void {
  if (!open.value) return;
  if (!panel.value?.contains(event.target as Node)) open.value = false;
}

onMounted(() => {
  void refresh();
  timer = window.setInterval(() => { void refresh(); }, POLL_MS);
  document.addEventListener('click', onDocumentClick);
});
onUnmounted(() => {
  if (timer !== null) window.clearInterval(timer);
  document.removeEventListener('click', onDocumentClick);
});

const badge = computed(() => (unread.value > 99 ? '99+' : String(unread.value)));

const grouped = computed(() => groupByProject(stackRepeats(items.value)));
</script>

<template>
  <div ref="panel" class="notif">
    <button
      class="notif-button"
      type="button"
      :aria-label="unread ? `通知，${unread} 条未读` : '通知'"
      :aria-expanded="open"
      @click="toggle"
    >
      <span aria-hidden="true">🔔</span>
      <b v-if="unread" class="notif-badge">{{ badge }}</b>
    </button>

    <section v-if="open" class="notif-panel" role="dialog" aria-label="通知">
      <header>
        <b>通知<template v-if="unread">（{{ unread }} 条未读）</template></b>
        <div>
          <button class="inline-button" type="button" @click="unreadOnly = !unreadOnly; refresh()">
            {{ unreadOnly ? '看全部' : '只看未读' }}
          </button>
          <button v-if="unread" class="inline-button" type="button" @click="markAll">全部已读</button>
        </div>
      </header>

      <p v-if="loading && !items.length" class="notif-hint">正在读取…</p>
      <p v-else-if="!items.length" class="notif-hint">
        {{ unreadOnly ? '没有未读通知。' : '还没有通知。' }}
      </p>

      <template v-else v-for="group in grouped" :key="group.label">
      <p class="notif-group">
        {{ group.label }}
        <span>{{ group.unread ? `${group.unread} 条未读` : `${group.items.length} 条` }}</span>
      </p>
      <ul class="notif-list">
        <li v-for="item in group.items" :key="item.id" :class="{ unread: !item.readAt, high: item.severity === 'high' }">
          <button type="button" class="notif-item" @click="openItem(item)">
            <span class="notif-meta">
              <i>{{ typeLabel(item.type) }}</i>
              <u v-if="item.repeats > 1" class="notif-repeat">已连续提醒 {{ item.repeats }} 天</u>
              <em v-if="item.projectNo">{{ item.projectNo }}</em>
              <time>{{ when(item.createdAt) }}</time>
            </span>
            <b>{{ item.title }}</b>
            <small v-if="item.body">{{ item.body }}</small>
          </button>
        </li>
      </ul>
      </template>
    </section>
  </div>
</template>

<style scoped>
.notif { position: relative; }
.notif-button {
  position: relative; min-height: 34px; padding: 4px 10px;
  border: 1px solid var(--line); border-radius: var(--radius-s);
  background: var(--white); cursor: pointer; font-size: 15px; line-height: 1;
}
.notif-button:hover { border-color: var(--blue); }
.notif-badge {
  position: absolute; top: -7px; right: -7px; min-width: 18px; padding: 0 5px;
  border-radius: 9px; background: var(--risk); color: white;
  font-size: 11px; font-weight: 800; line-height: 18px; text-align: center;
}
.notif-panel {
  position: absolute; top: calc(100% + 8px); right: 0; z-index: 40; width: min(420px, 92vw);
  max-height: min(560px, 76vh); overflow: auto;
  border: 1px solid var(--line); border-radius: var(--radius-s);
  background: var(--white); box-shadow: var(--shadow);
}
.notif-panel > header {
  position: sticky; top: 0; z-index: 3;
  display: flex; align-items: center; justify-content: space-between; gap: 10px;
  padding: 11px 14px; border-bottom: 1px solid var(--line);
  /* 不透明底色：否则滚动内容会从吸顶表头底下透出来，字叠字 */
  background: var(--white);
}
.notif-panel > header b { font-size: 13px; }
.notif-panel > header div { display: flex; gap: 10px; }
.notif-hint { margin: 0; padding: 18px 14px; color: var(--muted); font-size: 13px; }
.notif-list { margin: 0; padding: 0; list-style: none; }
.notif-group {
  position: sticky; top: 44px; z-index: 2;
  display: flex; align-items: baseline; justify-content: space-between;
  margin: 0; padding: 7px 14px 5px; background: #eef4fb;
  color: var(--ink); font-size: 12px; font-weight: 800;
  border-bottom: 1px solid var(--line);
}
.notif-group span { color: var(--muted); font-weight: 600; }
.notif-repeat {
  padding: 0 6px; border-radius: 999px; background: rgba(209, 52, 56, .1);
  color: var(--risk); font-size: 11px; font-weight: 700; text-decoration: none;
}
.notif-list li { border-bottom: 1px solid var(--line); }
.notif-list li:last-child { border-bottom: 0; }
.notif-list li.unread { background: rgba(0, 117, 174, .04); }
.notif-item {
  display: block; width: 100%; padding: 10px 14px;
  border: 0; background: transparent; cursor: pointer; text-align: left;
}
.notif-item:hover { background: rgba(0, 117, 174, .07); }
.notif-meta { display: flex; align-items: center; gap: 8px; margin-bottom: 3px; }
.notif-meta i { color: var(--blue); font-style: normal; font-size: 12px; font-weight: 800; }
.notif-meta em { color: var(--muted); font-style: normal; font-size: 12px; }
.notif-meta time { margin-left: auto; color: var(--muted); font-size: 12px; }
.notif-item b { display: block; color: var(--ink); font-size: 13px; }
.notif-item small { display: block; margin-top: 2px; color: var(--muted); font-size: 12px; line-height: 1.6; }
.notif-list li.high .notif-meta i { color: var(--risk); }
</style>
